import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import '../styles/dashboard.css';

interface DashboardLayoutProps {
  children: React.ReactNode;
}

const DashboardLayout: React.FC<DashboardLayoutProps> = ({ children }) => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [showMobileSidebar, setShowMobileSidebar] = useState(false);

  // Close user menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (showUserMenu && !target.closest('.sidebar-footer')) {
        setShowUserMenu(false);
      }
    };

    if (showUserMenu) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [showUserMenu]);

  const navItems = [
    { path: '/dashboard', label: 'System Overview', icon: '📊' },
    { path: '/dashboard/training', label: 'Training', icon: '🎓' },
    { path: '/dashboard/models', label: 'Models', icon: '🤖' },
    { path: '/dashboard/inference', label: 'Inference', icon: '🚀' },
    { path: '/dashboard/api', label: 'API', icon: '🔌' },
    { path: '/dashboard/model-builder', label: 'Model Builder', icon: '🔗' },
    { path: '/dashboard/pipeline-builder', label: 'Pipeline Builder', icon: '⚙️' },
    { path: '/dashboard/projects', label: 'Projects', icon: '📦' },
    { path: '/dashboard/data', label: 'Data', icon: '🗄️' },
    { path: '/dashboard/data-labeling', label: 'Data Labeling', icon: '🏷️' },
    { path: '/dashboard/community', label: 'Community', icon: '👥' },
    { path: '/dashboard/processes', label: 'Processes', icon: '🔄' },
    { path: '/dashboard/billing', label: 'Billing', icon: '💳' },
    { path: '/dashboard/settings', label: 'Settings', icon: '⚙️' },
    { path: '/dashboard/documentation', label: 'Documentation', icon: '📚' },
  ];

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/signin');
    } catch (error) {
      console.error('Logout failed:', error);
    }
  };

  const isActive = (path: string) => {
    if (path === '/dashboard') {
      return location.pathname === path || location.pathname === `${path}/`;
    }
    // For exact paths like /dashboard/processes, /dashboard/documentation, etc., use exact match
    // For paths with sub-routes like /dashboard/training, use startsWith
    const exactPaths = ['/dashboard/processes', '/dashboard/documentation', '/dashboard/billing', '/dashboard/settings', '/dashboard/models', '/dashboard/api', '/dashboard/model-builder', '/dashboard/pipeline-builder', '/dashboard/projects', '/dashboard/inference', '/dashboard/data-labeling', '/dashboard/community'];
    if (exactPaths.includes(path)) {
      return location.pathname === path || location.pathname.startsWith(`${path}/`);
    }
    return location.pathname === path || location.pathname.startsWith(`${path}/`);
  };

  return (
    <div className="dashboard-layout">
        {/* Mobile Sidebar Toggle */}
        <button className="mobile-toggle" onClick={() => setShowMobileSidebar(!showMobileSidebar)}>
          ☰
        </button>

        {/* Sidebar */}
        <aside className={`sidebar ${showMobileSidebar ? 'open' : ''}`}>
        <div className="sidebar-header">
          <div className="sidebar-logo-wrapper">
            <h1 className="logo">MLblocks</h1>
          </div>
        </div>

        <nav className="sidebar-nav">
          {navItems.map((item) => (
            <button
              key={item.path}
              className={`nav-item ${isActive(item.path) ? 'active' : ''}`}
              onClick={() => {
                navigate(item.path);
                setShowMobileSidebar(false);
              }}
            >
              <span className="nav-icon">{item.icon}</span>
              <span className="nav-label">{item.label}</span>
            </button>
          ))}
        </nav>

        <div className="sidebar-footer">
          <div className="user-info" onClick={() => setShowUserMenu(!showUserMenu)} style={{ cursor: 'pointer' }}>
            <div className="user-avatar">
              {user?.email?.charAt(0).toUpperCase()}
            </div>
            <div className="user-details">
              <div className="user-name">
                {user?.user_metadata?.name || 'User'}
              </div>
              <div className="user-email">{user?.email}</div>
            </div>
          </div>
          {showUserMenu && (
            <div className="sidebar-user-dropdown">
              <div className="dropdown-item">
                <strong>{user?.email}</strong>
              </div>
              <button
                className="dropdown-logout"
                onClick={() => {
                  handleLogout();
                  setShowUserMenu(false);
                }}
              >
                🚪 Logout
              </button>
            </div>
          )}
        </div>
      </aside>

      {/* Main Content */}
      <main className="main-content">
        <div className="content-area">
          {children}
        </div>
      </main>

        {/* Mobile Overlay */}
        {showMobileSidebar && (
          <div
            className="sidebar-overlay"
            onClick={() => setShowMobileSidebar(false)}
          />
        )}
      </div>
  );
};

export default DashboardLayout;
